/**
 * Upload Queue — global background uploader.
 *
 * Lives at the App root so a chunked video upload survives route
 * changes. The editor can fire off a 4K reel from
 * /admin/gallery/yacht-2025, navigate to /admin/dashboard, and the
 * upload keeps going. The bottom-right `UploadQueueTray` surfaces
 * live progress.
 *
 * Architecture:
 *   • A single FIFO queue of tasks. Concurrency = 1 (predictable
 *     bandwidth, plays nicely with the resumable chunkedUpload).
 *   • Each task is { id, kind: 'chunk'|'batch', galleryId, file(s),
 *     status, progress, error?, result? }.
 *   • A `useEffect` runs the worker — picks the next 'queued' task,
 *     drives it via chunkedUpload() or the existing /multi-upload
 *     batch endpoint, then loops to the next one.
 *   • Per-gallery subscribe() lets `AdminGalleryManager` learn when
 *     a task for its gallery completed so it can reload the grid.
 *
 * Persistence: tasks themselves can't be persisted (File objects
 * don't survive a reload), BUT chunkedUpload.js already caches the
 * sessionId in localStorage keyed by file fingerprint. So if the
 * user reloads mid-upload, they re-pick the same file and resume
 * picks up where they left off — that's iter 78's promise.
 */
import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { chunkedUpload, shouldChunk } from "@/lib/chunkedUpload";
import { analyzeFilePreview } from "@/lib/uploadPreviewAnalyze";

// Inline ID generator — avoids adding the uuid package just for this.
// crypto.randomUUID() is supported in every browser we ship to (Chrome 92+,
// Safari 15.4+, Firefox 95+) and Node 14.17+ for SSR contexts.
function makeId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `task-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const UploadQueueContext = createContext(null);

const STATUS = {
  QUEUED: "queued",
  RUNNING: "running",
  RESUMING: "resuming",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "cancelled",
};

const MAX_RECENT_VISIBLE = 6; // tray caps history; older completed tasks fall off

export function UploadQueueProvider({ children }) {
  const [tasks, setTasks] = useState([]);
  // Listeners keyed by galleryId — gallery pages subscribe to learn
  // when a task targeting their gallery completed so they can reload.
  const listenersRef = useRef(new Map());
  // Tracks whether the worker loop is already active so we don't
  // double-process.
  const workerRunningRef = useRef(false);
  // Latest tasks snapshot for the worker loop — using a ref means we
  // don't have to put `tasks` in the worker effect's deps (which would
  // cancel the in-flight upload on every progress update).
  const tasksRef = useRef([]);
  // Ref to the trigger function so callbacks can kick the worker
  // without creating circular dependencies in useCallback.
  const triggerRef = useRef(null);

  // Keep tasksRef in sync with state.
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);

  const updateTask = useCallback((id, patch) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }, []);

  const notify = useCallback((galleryId, payload) => {
    const subs = listenersRef.current.get(galleryId);
    if (!subs) return;
    for (const cb of subs) {
      try { cb(payload); } catch { /* never let a subscriber explode the worker */ }
    }
  }, []);

  /**
   * subscribe(galleryId, callback) — gallery pages call this in a
   * useEffect to learn when uploads complete. Returns an unsubscribe.
   */
  const subscribe = useCallback((galleryId, callback) => {
    if (!listenersRef.current.has(galleryId)) {
      listenersRef.current.set(galleryId, new Set());
    }
    listenersRef.current.get(galleryId).add(callback);
    return () => {
      const subs = listenersRef.current.get(galleryId);
      if (subs) {
        subs.delete(callback);
        if (subs.size === 0) listenersRef.current.delete(galleryId);
      }
    };
  }, []);

  /**
   * enqueue(galleryId, files, opts?) — drop files into the queue.
   *
   * Splits files: small images go into a single 'batch' task per
   * gallery (one round-trip via /multi-upload), large files / videos
   * each get their own 'chunk' task (so the user sees per-file
   * progress and can cancel individual videos).
   *
   * Returns the list of created task IDs.
   */
  const enqueue = useCallback((galleryId, files, opts = {}) => {
    if (!galleryId || !files || files.length === 0) return [];
    const fileArr = Array.from(files);
    const chunked = fileArr.filter(shouldChunk);
    const batch = fileArr.filter((f) => !shouldChunk(f));
    const created = [];

    if (batch.length > 0) {
      const t = {
        id: makeId(),
        kind: "batch",
        galleryId,
        files: batch,
        filename: batch.length === 1 ? batch[0].name : `${batch.length} images`,
        size: batch.reduce((acc, f) => acc + f.size, 0),
        status: STATUS.QUEUED,
        progress: 0,
        statusLabel: "",
        galleryTitle: opts.galleryTitle || "",
        createdAt: Date.now(),
      };
      created.push(t.id);
      setTasks((prev) => [...prev, t]);
    }

    for (const f of chunked) {
      const t = {
        id: makeId(),
        kind: "chunk",
        galleryId,
        files: [f],
        file: f,
        filename: f.name,
        size: f.size,
        status: STATUS.QUEUED,
        progress: 0,
        statusLabel: "",
        galleryTitle: opts.galleryTitle || "",
        createdAt: Date.now(),
      };
      created.push(t.id);
      setTasks((prev) => [...prev, t]);
    }

    return created;
  }, []);

  const cancelTask = useCallback((id) => {
    setTasks((prev) => prev.map((t) => {
      if (t.id !== id) return t;
      // Only queued tasks can be cancelled outright; running tasks
      // are flagged 'cancelled' but their network request will still
      // run to completion (we'd need an AbortController plumbed into
      // chunkedUpload to truly stop mid-flight — that's a future
      // enhancement).
      if (t.status === STATUS.QUEUED) {
        return { ...t, status: STATUS.CANCELLED };
      }
      return t;
    }));
  }, []);

  const removeTask = useCallback((id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const retryTask = useCallback((id) => {
    setTasks((prev) => prev.map((t) =>
      t.id === id && (t.status === STATUS.FAILED || t.status === STATUS.CANCELLED)
        ? { ...t, status: STATUS.QUEUED, progress: 0, error: undefined, statusLabel: "" }
        : t,
    ));
  }, []);

  const clearCompleted = useCallback(() => {
    setTasks((prev) => prev.filter((t) =>
      t.status !== STATUS.COMPLETED
      && t.status !== STATUS.CANCELLED
    ));
  }, []);

  // ---- Worker loop ----
  // Imperative trigger pattern: called when a task is enqueued or when
  // the previous task finishes. Uses tasksRef (not the `tasks` closure)
  // so the worker doesn't get cancelled by progress-update re-renders.
  const triggerWorker = useCallback(() => {
    if (workerRunningRef.current) return;
    const next = tasksRef.current.find((t) => t.status === STATUS.QUEUED);
    if (!next) return;
    workerRunningRef.current = true;

    const run = async () => {
      updateTask(next.id, { status: STATUS.RUNNING, startedAt: Date.now() });

      // Fire ARIA pre-processing in parallel — surfaces theme,
      // hashtags, and suggested actions while the chunks upload.
      // Only meaningful for single-file (chunk) tasks; batch
      // tasks are usually small images where the preview would
      // arrive after the upload is already done.
      if (next.kind === "chunk" && next.file) {
        analyzeFilePreview({ api, file: next.file })
          .then((preview) => {
            if (preview) updateTask(next.id, { preview });
          })
          .catch(() => { /* preview is best-effort */ });
      }

      try {
        if (next.kind === "chunk") {
          const media = await chunkedUpload({
            api,
            galleryId: next.galleryId,
            file: next.file,
            onProgress: (pct) => updateTask(next.id, { progress: pct }),
            onStatus: (label) => updateTask(next.id, { statusLabel: label || "" }),
          });
          updateTask(next.id, {
            status: STATUS.COMPLETED,
            progress: 100,
            result: media,
            statusLabel: "",
            completedAt: Date.now(),
          });
          notify(next.galleryId, { type: "upload-complete", task: next, media });
        } else if (next.kind === "batch") {
          const formData = new FormData();
          for (const f of next.files) formData.append("files", f);
          const res = await api.post(
            `/admin/galleries/${next.galleryId}/multi-upload`,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
              timeout: 300000,
              onUploadProgress: (p) => {
                if (p.total) {
                  updateTask(next.id, {
                    progress: Math.round((p.loaded / p.total) * 100),
                  });
                }
              },
            },
          );
          const data = res.data || {};
          updateTask(next.id, {
            status: STATUS.COMPLETED,
            progress: 100,
            result: data,
            statusLabel: "",
            completedAt: Date.now(),
            uploaded: data.uploaded || 0,
            total: data.total || next.files.length,
          });
          notify(next.galleryId, { type: "upload-complete", task: next, batch: data });
        }
      } catch (err) {
        const friendly =
          err?.response?.data?.detail
          || (err?.code === "ECONNABORTED" ? "Upload timed out"
            : err?.code === "ERR_NETWORK" ? "Network dropped"
              : err?.message || "Upload failed");
        updateTask(next.id, {
          status: STATUS.FAILED,
          error: friendly,
          statusLabel: "",
          failedAt: Date.now(),
        });
        notify(next.galleryId, { type: "upload-failed", task: next, error: friendly });
      } finally {
        workerRunningRef.current = false;
        // Garbage-collect: cap completed task history so the array
        // doesn't grow unboundedly over a long editing session.
        setTasks((prev) => {
          const live = prev.filter((t) =>
            t.status === STATUS.QUEUED
            || t.status === STATUS.RUNNING
            || t.status === STATUS.RESUMING
            || t.status === STATUS.FAILED,
          );
          const done = prev
            .filter((t) => t.status === STATUS.COMPLETED || t.status === STATUS.CANCELLED)
            .sort((a, b) => (b.completedAt || b.createdAt) - (a.completedAt || a.createdAt))
            .slice(0, MAX_RECENT_VISIBLE);
          return [...live, ...done];
        });
        // Pick up the next queued task, if any.
        triggerRef.current?.();
      }
    };

    run();
  }, [updateTask, notify]);

  // Keep triggerRef in sync so the finally-block can call it without
  // depending on the closure (which captures the initial reference).
  useEffect(() => { triggerRef.current = triggerWorker; }, [triggerWorker]);

  // Kick the worker on every tasks-state change BUT without using
  // tasks in deps for cancellation. We always re-evaluate "is there a
  // queued task?" but never cancel an in-flight one.
  useEffect(() => {
    triggerWorker();
  }, [tasks, triggerWorker]);

  const value = {
    tasks,
    enqueue,
    cancelTask,
    removeTask,
    retryTask,
    clearCompleted,
    subscribe,
    STATUS,
  };

  return (
    <UploadQueueContext.Provider value={value}>
      {children}
    </UploadQueueContext.Provider>
  );
}

export function useUploadQueue() {
  const ctx = useContext(UploadQueueContext);
  if (!ctx) {
    throw new Error("useUploadQueue must be used inside <UploadQueueProvider>");
  }
  return ctx;
}

export { STATUS as UPLOAD_STATUS };
